/**
 * Reusable labeled text input for the employee Create / Edit forms.
 *
 * Renders the same DOM that both pages used inline (the `renderField` helper
 * defined twice). Carries through `data-testid` so existing e2e tests keep
 * working.
 *
 * Edit-page extension: when `sensitive` is true a small shield icon is shown
 * next to the label, the input is disabled if the current user can't edit
 * sensitive fields, and (for masked identity/bank fields) a hint reminding
 * the user to leave the field empty to keep the current value is rendered.
 */
import React from 'react';
import { Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const MASKED_SENSITIVE_FIELDS = ['national_id', 'iqama', 'iban'];

export const EmployeeFormField = ({
  field,
  label,
  type = 'text',
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  placeholder,
  // Edit-page extensions
  sensitive = false,
  canEditSensitive = true,
  isRTL = false,
}) => {
  const hasError = Boolean(error);
  const showSensitiveHint = sensitive && MASKED_SENSITIVE_FIELDS.includes(field);
  const finalDisabled = disabled || (sensitive && !canEditSensitive && !showSensitiveHint);

  return (
    <div className="space-y-2">
      <Label
        htmlFor={field}
        className={cn(hasError && 'text-red-500', sensitive && 'flex items-center gap-1')}
      >
        {label}
        {required && <span className="text-red-500 ms-1">*</span>}
        {sensitive && <Shield className="h-3 w-3 text-amber-500" />}
      </Label>
      <Input
        id={field}
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(field, e.target.value)}
        className={cn(hasError && 'border-red-500')}
        placeholder={placeholder}
        disabled={finalDisabled}
        data-testid={`input-${field}`}
      />
      {showSensitiveHint && (
        <p className="text-xs text-slate-500">
          {isRTL ? 'اترك فارغاً للاحتفاظ بالقيمة الحالية' : 'Leave empty to keep current value'}
        </p>
      )}
      {hasError && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default EmployeeFormField;
